import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  GridRowsProp,
  GridApiPremium,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { NestedSubtableDetailPanel } from "../../../components/datagrid/DetailPanels/NestedSubtable";
import ServerDataGrid from "../../../components/datagrid/ServerDataGrid";
import {
  ListingsDataGrid,
  SupplyUnit,
} from "../../../components/datagrid/ListingsDataGrid";
import {
  columns as inventoryColumns,
  nestedTableColumns as nestedListingTableColumns,
} from "./inventoryColumns";
import {
  columns as listingColumns,
  nestedTableColumns as nestedInventoryTableColumns,
} from "./listingColumns";
import {
  Tab,
  Tabs,
  Box,
  Autocomplete,
  TextField,
  Snackbar,
  Alert,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuIcon from "@mui/icons-material/Menu";
import {
  ColumnSetsManager,
  ColumnSetEntry,
} from "../../../apis/ColumnSetsManager";
import { fetchUserAttributes } from "@aws-amplify/auth";
import { inventoryTableCreateFields } from "./inventoryCreateFields";
import { ListingTableButtonsPanel } from "./ListingTableButtonsPanel";
import { InventoryTableButtonsPanel } from "./InventoryTableButtonsPanel";
import { PricetoolSidebar } from "./PricetoolSidebar";
import { PricetoolModalSwitch, ModalSwitchState } from "./PricetoolModalSwitch";
import ListingContextMenu from "./contextMenu/ListingContextMenu";
import InventoryContextMenu from "./contextMenu/InventoryContextMenu";

type PricetoolTabs = 0 | 1 | 2;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Note: nestedInventoryColumns and nestedListingColumns are computed inside the component
// so they can use the reordered columns when a column set is selected

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ height: value === index ? "calc(100vh - 200px)" : "0" }}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, height: "100%" }}>{children}</Box>}
    </div>
  );
}

// Static style objects to prevent re-creation on every render
const HEADER_BOX_SX = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: 1,
  borderColor: "divider",
  padding: "7px",
  gap: 2,
  maxWidth: "100%",
} as const;

const LEFT_SECTION_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flex: 1,
} as const;

const RIGHT_SECTION_SX = {
  display: "flex",
  alignItems: "center",
  gap: 1,
} as const;

const SEARCH_FIELD_SX = {
  minWidth: 250,
  maxWidth: 400,
} as const;

const COLUMN_SET_AUTOCOMPLETE_SX = {
  minWidth: 250,
} as const;

const LISTINGS_DEFAULT_EDIT_VISIBILITY: Record<string, boolean> = {
  "product.product_title": true,
  "product.wn": true,
  "product.wwo": true,
  media_types: true,
  "product.card_number": true,
  "product.priority": true,
  "product.shelf": true,
};

const INVENTORY_DEFAULT_EDIT_VISIBILITY: Record<string, boolean> = {
  "supply.supply_title": true,
  "inventory.inventory_shelf": true,
  "supply.alliance": true,
  "supply.amazon": true,
  "supply.ccvideo": true,
  "supply.dealsareus": true,
  "supply.deepdiscount": true,
  "supply.discount_entertainment": true,
  "supply.popmarket": true,
  "supply.inetvideo": true,
  "supply.importcds": true,
  "supply.mila": true,
  "supply.gruv": true,
  "supply.wildcard1": true,
  "supply.wildcard2": true,
  "supply.rarewaves": true,
  "supply.target_store": true,
  "supply.vei": true,
  "supply.walmart": true,
  "supply.wowhd": true,
  "supply.supply_notes": true,
  media_types: true,
};

export function PricetoolPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Create stable cache references for nested table data
  // One cache for inventory nested panels (showing listings)
  const inventoryNestedCache = useRef(
    new Map<string, { rows: GridRowsProp; totalCount: number }>()
  );
  // One cache for listings nested panels (showing inventory)
  const listingsNestedCache = useRef(
    new Map<string, { rows: GridRowsProp; totalCount: number }>()
  );

  // Grid API refs for accessing selected rows
  const inventoryGridApiRef = useRef<GridApiPremium | null>(null);
  const listingsGridApiRef = useRef<GridApiPremium | null>(null);

  // Sidebar visibility state
  const [leftSidebarVisible, setLeftSidebarVisible] = useState(false);
  const [rightSidebarVisible, setRightSidebarVisible] = useState(false);

  // Output log state for PricetoolLog sidebar
  const [outputLog, setOutputLog] = useState<any[]>([]);

  // Callback to append messages to the output log
  const appendOutputLog = useCallback((logEntry: any) => {
    setOutputLog((prev) => [logEntry, ...prev]);
  }, []);

  // Read current inventory grid selection and map to SupplyUnit[] for CreateProductWizard
  const getInventorySelectedRows = useCallback((): SupplyUnit[] => {
    const api = inventoryGridApiRef.current;
    if (!api) return [];
    const { ids } = api.state.rowSelection as GridRowSelectionModel;
    return Array.from(ids)
      .map((id) => api.getRow(id))
      .filter(
        (row): row is NonNullable<ReturnType<typeof api.getRow>> => row != null
      )
      .map((row) => ({
        upc: row.supply?.upc ?? "",
        supply_title: row.supply?.supply_title ?? "",
        part_number: row.inventory?.part_number ?? "",
        inventory_id: row.inventory?.inventory_id ?? null,
        inventory_title: row.inventory?.inventory_title ?? "",
      }));
  }, []);

  // ModalSwitch state
  const [modalSwitchState, setModalSwitchState] = useState<ModalSwitchState>({
    modalName: "",
    selectedRows: [],
    appendStatus: false,
    appendErrors: [],
    columnNames: [],
    mode: "",
    barCodeType: undefined,
    rowData: undefined,
    slotLabelType: undefined,
    message: undefined,
    messageType: undefined,
    messageCopy: undefined,
  });

  // Callback to close modal
  const closeModal = useCallback(() => {
    setModalSwitchState({
      modalName: "",
      selectedRows: [],
      appendStatus: false,
      appendErrors: [],
      columnNames: [],
      mode: "",
    });
  }, []);

  // General search state (for searching across all tables)
  const [generalSearchInput, setGeneralSearchInput] = useState("");
  const [debouncedGeneralSearch, setDebouncedGeneralSearch] = useState("");

  // Debounce general search input
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedGeneralSearch(generalSearchInput);
    }, 500);

    return () => clearTimeout(timeout);
  }, [generalSearchInput]);

  // Map between tab names and indices
  const tabNameToIndex: Record<string, PricetoolTabs> = {
    listings: 0,
    inventory: 1,
    all: 2,
  };

  const tabIndexToName: Record<PricetoolTabs, string> = {
    0: "listings",
    1: "inventory",
    2: "all",
  };

  // Initialize tab from URL, then localStorage default, then 0
  const getInitialTab = (): PricetoolTabs => {
    const tabParam = searchParams.get("tab");
    if (tabParam && tabParam in tabNameToIndex) {
      return tabNameToIndex[tabParam];
    }
    const stored = localStorage.getItem("pricetool-default-tab");
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (parsed === 0 || parsed === 1 || parsed === 2)
        return parsed as PricetoolTabs;
    }
    return 0;
  };

  const [tab, setTab] = useState<PricetoolTabs>(getInitialTab);

  // Keep filters/sorts on tab switch preference (defaults to true)
  const KEEP_FILTERS_STORAGE_KEY = "pricetool_keep_filters_on_tab_switch";
  const [keepFiltersOnTabSwitch, setKeepFiltersOnTabSwitch] = useState<boolean>(
    () => {
      const stored = localStorage.getItem(KEEP_FILTERS_STORAGE_KEY);
      // If nothing in localStorage, default to true
      // Only store in localStorage when set to false
      return stored === null ? true : stored === "true";
    }
  );

  // Default tab preference
  const DEFAULT_TAB_STORAGE_KEY = "pricetool-default-tab";
  const [defaultTab, setDefaultTab] = useState<PricetoolTabs | null>(() => {
    const stored = localStorage.getItem(DEFAULT_TAB_STORAGE_KEY);
    if (stored !== null) {
      const parsed = parseInt(stored, 10);
      if (parsed === 0 || parsed === 1 || parsed === 2)
        return parsed as PricetoolTabs;
    }
    return null;
  });
  const isCurrentTabDefault = defaultTab === tab;

  // Column sets state
  const [columnSets, setColumnSets] = useState<ColumnSetEntry[]>([]);
  const [selectedColumnSet, setSelectedColumnSet] =
    useState<ColumnSetEntry | null>(null);
  const [
    showInvalidColumnSetNotification,
    setShowInvalidColumnSetNotification,
  ] = useState(false);

  // Load user's column sets on mount and initialize from URL
  useEffect(() => {
    const loadColumnSets = async () => {
      try {
        // Get current user from Amplify
        const user = await fetchUserAttributes();
        const userId = user.email?.split("@")[0];

        if (userId) {
          // Fetch column sets for this user
          const userColumnSets = await ColumnSetsManager.getColumnSetsForUserId(
            userId
          );
          setColumnSets(userColumnSets);

          // Check if there's a column set in the URL
          const columnSetParam = searchParams.get("colset");
          if (columnSetParam && userColumnSets.length > 0) {
            const matchingColumnSet = userColumnSets.find(
              (cs) => cs.columnSetName === columnSetParam
            );

            if (matchingColumnSet) {
              setSelectedColumnSet(matchingColumnSet);
            } else {
              // Column set doesn't exist, show notification
              setShowInvalidColumnSetNotification(true);
            }
          }
        }
      } catch (error) {
        console.error("Error loading column sets:", error);
      }
    };

    loadColumnSets();
  }, []);

  // Calculate column visibility models and reordered columns based on selected column set (memoized to prevent re-renders)
  const {
    inventoryModel: inventoryColumnVisibilityModel,
    listingModel: listingColumnVisibilityModel,
    orderedInventoryColumns,
    orderedListingColumns,
  } = useMemo(() => {
    if (!selectedColumnSet) {
      return {
        inventoryModel: {},
        listingModel: {},
        orderedInventoryColumns: inventoryColumns,
        orderedListingColumns: listingColumns,
      };
    }

    const inventoryModel = ColumnSetsManager.transformToColumnVisibilityModel(
      inventoryColumns,
      selectedColumnSet.columns.inventoryColumns
    );
    const listingModel = ColumnSetsManager.transformToColumnVisibilityModel(
      listingColumns,
      selectedColumnSet.columns.listingColumns
    );

    // Reorder columns based on the column set order
    const orderedInventoryColumns = ColumnSetsManager.reorderColumns(
      inventoryColumns,
      selectedColumnSet.columns.inventoryColumns
    );
    const orderedListingColumns = ColumnSetsManager.reorderColumns(
      listingColumns,
      selectedColumnSet.columns.listingColumns
    );

    return {
      inventoryModel,
      listingModel,
      orderedInventoryColumns,
      orderedListingColumns,
    };
  }, [selectedColumnSet]);

  // Compute nested columns based on the ordered columns, preserving nestedTableColumns order
  const nestedInventoryColumns = useMemo(() => {
    const colMap = new Map(
      orderedInventoryColumns.map((col) => [col.field, col])
    );
    return nestedInventoryTableColumns
      .map((field) => colMap.get(field))
      .filter((col): col is NonNullable<typeof col> => col !== undefined);
  }, [orderedInventoryColumns]);

  const nestedListingColumns = useMemo(() => {
    const colMap = new Map(
      orderedListingColumns.map((col) => [col.field, col])
    );
    return nestedListingTableColumns
      .map((field) => colMap.get(field))
      .filter((col): col is NonNullable<typeof col> => col !== undefined);
  }, [orderedListingColumns]);

  // On mount, ensure tab is always the first URL param
  useEffect(() => {
    const currentParams = new URLSearchParams(window.location.search);
    const newParams = new URLSearchParams();
    newParams.set("tab", tabIndexToName[tab]);
    currentParams.forEach((value, key) => {
      if (key !== "tab") newParams.set(key, value);
    });
    setSearchParams(newParams, { replace: true });
  }, []);

  // Handler for keep filters/sorts checkbox change
  const handleKeepFiltersChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.checked;
      setKeepFiltersOnTabSwitch(newValue);

      if (newValue) {
        // Remove from localStorage when set back to true (default)
        localStorage.removeItem(KEEP_FILTERS_STORAGE_KEY);
      } else {
        // Store in localStorage when set to false
        localStorage.setItem(KEEP_FILTERS_STORAGE_KEY, "false");
      }
    },
    [KEEP_FILTERS_STORAGE_KEY]
  );

  // Handler for default tab checkbox change
  const handleDefaultTabChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        localStorage.setItem(DEFAULT_TAB_STORAGE_KEY, String(tab));
        setDefaultTab(tab);
      } else {
        localStorage.removeItem(DEFAULT_TAB_STORAGE_KEY);
        setDefaultTab(null);
      }
    },
    [tab, DEFAULT_TAB_STORAGE_KEY]
  );

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: PricetoolTabs) => {
      setTab(newValue);
      const newParams = new URLSearchParams();

      if (keepFiltersOnTabSwitch) {
        // Keep existing params when enabled
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.forEach((value, key) => {
          newParams.set(key, value);
        });
      }

      // Always set the tab param
      newParams.set("tab", tabIndexToName[newValue]);
      setSearchParams(newParams);
    },
    [tabIndexToName, setSearchParams, keepFiltersOnTabSwitch]
  );

  // Update URL when column set changes
  const handleColumnSetChange = useCallback(
    (_event: React.SyntheticEvent, newValue: ColumnSetEntry | null) => {
      setSelectedColumnSet(newValue);

      const newParams = new URLSearchParams(window.location.search);

      // Add or remove column set parameter
      if (newValue) {
        newParams.set("colset", newValue.columnSetName);
      } else {
        newParams.delete("colset");
      }

      setSearchParams(newParams);
    },
    [setSearchParams]
  );

  // Memoize getRowId functions to prevent DataGrid re-renders
  const inventoryGetRowId = useCallback((row: any) => row.supply.upc, []);
  const listingGetRowId = useCallback(
    (row: any) => row.amazon_listings.listing_id,
    []
  );
  const nestedListingGetRowId = useCallback(
    (row: any) => row.amazon_listings?.listing_id,
    []
  );
  const nestedInventoryGetRowId = useCallback(
    (row: any) => row.supply?.upc,
    []
  );

  // Memoize getNestedRelationKey functions
  const inventoryNestedRelationKey = useCallback(
    (params: any) => params.row?.supply.inventory_id,
    []
  );
  const listingNestedRelationKey = useCallback(
    (params: any) => params.row?.product.product_id,
    []
  );

  // Memoize getDetailPanelContent callbacks
  const inventoryDetailPanelContent = useCallback(
    (params: any, apiRef: any) => {
      return (
        <NestedSubtableDetailPanel
          params={params}
          apiEndpoint="/datagrid/inventory"
          nestedRelationName="product"
          getNestedRelationKey={inventoryNestedRelationKey}
          getRowId={nestedListingGetRowId}
          columns={nestedListingColumns}
          parentApiRef={apiRef}
          cache={inventoryNestedCache.current}
          initialStateOverride={{
            sorting: {
              sortModel: [{ field: "amazon_listings.sort", sort: "asc" }],
            },
          }}
        />
      );
    },
    [inventoryNestedRelationKey, nestedListingGetRowId]
  );

  const listingDetailPanelContent = useCallback(
    (params: any, apiRef: any) => (
      <NestedSubtableDetailPanel
        params={params}
        apiEndpoint="/datagrid/listings"
        nestedRelationName="inventory"
        getRowId={nestedInventoryGetRowId}
        getNestedRelationKey={listingNestedRelationKey}
        columns={nestedInventoryColumns}
        parentApiRef={apiRef}
        cache={listingsNestedCache.current}
        initialStateOverride={{
          sorting: { sortModel: [{ field: "supply.sort", sort: "asc" }] },
        }}
      />
    ),
    [nestedInventoryGetRowId, listingNestedRelationKey]
  );

  // Memoize slotProps for TextField to prevent re-creation
  const searchFieldSlotProps = useMemo(
    () => ({
      input: {
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon fontSize="small" />
          </InputAdornment>
        ),
      },
    }),
    []
  );

  return (
    <>
      <PricetoolSidebar
        leftSidebarVisible={leftSidebarVisible}
        rightSidebarVisible={rightSidebarVisible}
        onCloseLeftSidebar={() => setLeftSidebarVisible(false)}
        onCloseRightSidebar={() => setRightSidebarVisible(false)}
        inventoryGridApiRef={inventoryGridApiRef}
        outputLog={outputLog}
        setOutputLog={setOutputLog}
      />
      <PricetoolModalSwitch
        modalState={modalSwitchState}
        closeModal={closeModal}
      />

      {/* General Search Header with Column Set Selector */}
      <Box sx={HEADER_BOX_SX}>
        {/* Left Section: Hamburger + General Search */}
        <Box sx={LEFT_SECTION_SX}>
          <IconButton
            onClick={() => setLeftSidebarVisible(!leftSidebarVisible)}
            size="small"
          >
            <MenuIcon />
          </IconButton>
          <TextField
            size="small"
            placeholder="Search all tables..."
            value={generalSearchInput}
            onChange={(e) => setGeneralSearchInput(e.target.value)}
            sx={SEARCH_FIELD_SX}
            slotProps={searchFieldSlotProps}
          />
        </Box>

        {/* Right Section: Column Set Selector + Hamburger */}
        <Box sx={RIGHT_SECTION_SX}>
          <Autocomplete
            options={columnSets}
            getOptionLabel={(option) => option.columnSetName}
            value={selectedColumnSet}
            onChange={handleColumnSetChange}
            sx={COLUMN_SET_AUTOCOMPLETE_SX}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Column Set"
                placeholder="Search column sets..."
                variant="outlined"
                size="small"
              />
            )}
            isOptionEqualToValue={(option, value) =>
              option.columnSetName === value.columnSetName
            }
          />
          <IconButton
            onClick={() => setRightSidebarVisible(!rightSidebarVisible)}
            size="small"
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          aria-label="basic tabs example"
        >
          <Tab label="Listings" />
          <Tab label="Inventory" />
          <Tab label="All" />
        </Tabs>
        <FormControlLabel
          control={
            <Checkbox
              checked={keepFiltersOnTabSwitch}
              onChange={handleKeepFiltersChange}
              size="small"
            />
          }
          label="Keep filters/sorts on tab switch"
          sx={{ ml: 1, whiteSpace: "nowrap" }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={isCurrentTabDefault}
              onChange={handleDefaultTabChange}
              size="small"
            />
          }
          label="Default tab"
          sx={{ whiteSpace: "nowrap" }}
        />
      </Box>

      {/* Listings Tab  */}
      <CustomTabPanel value={tab} index={0}>
        <ListingsDataGrid
          columns={orderedListingColumns}
          apiEndpoint="/datagrid/listings"
          getRowId={listingGetRowId}
          tableName="listings"
          initialStateOverride={{
            sorting: {
              sortModel: [
                { field: "amazon_listings.asin_7_day_sales", sort: "desc" },
              ],
            },
          }}
          columnVisibilityModel={listingColumnVisibilityModel}
          getDetailPanelContent={listingDetailPanelContent}
          enableDuplicate
          enableCreate
          ExtraButtonsPanel={ListingTableButtonsPanel}
          extraButtonsPanelProps={{ appendOutputLog, setModalSwitchState }}
          externalSearchValue={debouncedGeneralSearch}
          ContextMenuComponent={ListingContextMenu}
          defaultEditFieldVisibility={LISTINGS_DEFAULT_EDIT_VISIBILITY}
          getInventorySelectedRows={getInventorySelectedRows}
        />
      </CustomTabPanel>
      {/* Inventory Tab  */}
      <CustomTabPanel value={tab} index={1}>
        <ServerDataGrid
          columns={orderedInventoryColumns}
          apiEndpoint="/datagrid/inventory"
          getRowId={inventoryGetRowId}
          tableName="inventory"
          initialStateOverride={{
            sorting: {
              sortModel: [{ field: "inventory.add_date", sort: "desc" }],
            },
          }}
          columnVisibilityModel={inventoryColumnVisibilityModel}
          getDetailPanelContent={inventoryDetailPanelContent}
          enableDuplicate
          enableCreate
          createFormFields={inventoryTableCreateFields}
          ExtraButtonsPanel={InventoryTableButtonsPanel}
          extraButtonsPanelProps={{ appendOutputLog, setModalSwitchState }}
          apiRef={inventoryGridApiRef}
          externalSearchValue={debouncedGeneralSearch}
          ContextMenuComponent={InventoryContextMenu}
          defaultEditFieldVisibility={INVENTORY_DEFAULT_EDIT_VISIBILITY}
        />
      </CustomTabPanel>
      {/* All Tab */}
      <CustomTabPanel value={tab} index={2}>
        {/* Listings Table */}
        <ListingsDataGrid
          height={"60vh"}
          columns={orderedListingColumns}
          apiEndpoint="/datagrid/listings"
          getRowId={listingGetRowId}
          tableName="listings"
          initialStateOverride={{
            sorting: {
              sortModel: [
                { field: "amazon_listings.asin_7_day_sales", sort: "desc" },
              ],
            },
          }}
          columnVisibilityModel={listingColumnVisibilityModel}
          getDetailPanelContent={listingDetailPanelContent}
          enableDuplicate
          enableCreate
          ExtraButtonsPanel={ListingTableButtonsPanel}
          extraButtonsPanelProps={{ appendOutputLog, setModalSwitchState }}
          externalSearchValue={debouncedGeneralSearch}
          ContextMenuComponent={ListingContextMenu}
          defaultEditFieldVisibility={LISTINGS_DEFAULT_EDIT_VISIBILITY}
          getInventorySelectedRows={getInventorySelectedRows}
        />
        {/* Inventory Table */}
        <ServerDataGrid
          height={"60vh"}
          columns={orderedInventoryColumns}
          apiEndpoint="/datagrid/inventory"
          getRowId={inventoryGetRowId}
          tableName="inventory"
          columnVisibilityModel={inventoryColumnVisibilityModel}
          getDetailPanelContent={inventoryDetailPanelContent}
          enableDuplicate
          enableCreate
          createFormFields={inventoryTableCreateFields}
          ExtraButtonsPanel={InventoryTableButtonsPanel}
          extraButtonsPanelProps={{ appendOutputLog, setModalSwitchState }}
          apiRef={inventoryGridApiRef}
          externalSearchValue={debouncedGeneralSearch}
          ContextMenuComponent={InventoryContextMenu}
          defaultEditFieldVisibility={INVENTORY_DEFAULT_EDIT_VISIBILITY}
        />
      </CustomTabPanel>

      {/* Notification for invalid column set */}
      <Snackbar
        open={showInvalidColumnSetNotification}
        autoHideDuration={6000}
        onClose={() => setShowInvalidColumnSetNotification(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowInvalidColumnSetNotification(false)}
          severity="warning"
          sx={{ width: "100%" }}
        >
          The column set from the URL does not exist. Defaulting to no column
          set.
        </Alert>
      </Snackbar>
    </>
  );
}
